'use client'

import { TrendingDownSolidIcon } from '@louez/ui/icons'
import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { Bath, BedDouble, Calendar, ChevronLeft, ChevronRight, Ruler, Users } from 'lucide-react'

import { Card, CardContent } from '@louez/ui'
import { Badge } from '@louez/ui'
import { Button } from '@louez/ui'
import { formatCurrency, minutesToPriceDuration } from '@louez/utils'
import { useStoreCurrency, useStoreMaxDiscountPercent } from '@/contexts/store-context'
import { ProductImage } from '@/components/product/product-image'
import { ProductPreviewModal } from './product-preview-modal'
import type { NauticalProductDetails, PricingMode } from '@louez/types'
import type { BusinessHours } from '@louez/types'
import { getStorefrontPricingSummary } from '@/lib/utils/storefront-pricing'

interface PricingTier {
  id: string
  minDuration: number | null
  discountPercent: string | null
  period?: number | null
  price?: string | null
  displayOrder: number | null
}

interface Product {
  id: string
  name: string
  description: string | null
  price: string
  images: string[] | null
  quantity: number
  deposit: string | null
  pricingMode?: PricingMode | null
  basePeriodMinutes?: number | null
  pricingTiers?: PricingTier[]
  videoUrl?: string | null
  category?: { name: string } | null
  nauticalDetails?: NauticalProductDetails | null
}

interface ProductGridWithPreviewProps {
  products: Product[]
  storeSlug: string
  /** Prefix product links need on this host (empty on a store subdomain). */
  basePath?: string
  businessHours?: BusinessHours
  advanceNotice?: number
  minRentalMinutes?: number
  timezone?: string
  initialProductId?: string
}

function ProductCardInteractive({
  product,
  href,
  onClick,
}: {
  product: Product
  href: string
  onClick: () => void
}) {
  const t = useTranslations('storefront.product')
  const tCatalog = useTranslations('storefront.catalog')
  const tCommon = useTranslations('common')
  const currency = useStoreCurrency()
  const maxDiscountPercent = useStoreMaxDiscountPercent()
  const [imageIndex, setImageIndex] = useState(0)
  const images = product.images?.filter(Boolean) ?? []
  const mainImage = images[imageIndex]
  const isAvailable = product.quantity > 0

  const pricingSummary = getStorefrontPricingSummary(product)
  const cardDiscount = maxDiscountPercent == null
    ? pricingSummary.maxReductionPercent
    : Math.max(...pricingSummary.allReductionPercents.filter((p) => p <= maxDiscountPercent), 0)
  const displayPeriod = minutesToPriceDuration(pricingSummary.displayPeriodMinutes)
  const periodLabel =
    displayPeriod.unit === 'minute'
      ? displayPeriod.duration === 1
        ? tCommon('minuteUnit', { count: 1 })
        : `${displayPeriod.duration} ${tCommon('minuteUnit', { count: displayPeriod.duration })}`
      : displayPeriod.duration === 1
        ? t(`pricingUnit.${displayPeriod.unit}.singular`)
        : `${displayPeriod.duration} ${t(`pricingUnit.${displayPeriod.unit}.plural`)}`

  const priceForPeriod = (minutes: number) => {
    const tier = product.pricingTiers?.find((item) => item.period === minutes && item.price)
    if (tier?.price) return Number(tier.price)
    if (product.basePeriodMinutes === minutes) return Number(product.price)
    return null
  }
  const dayPrice = priceForPeriod(1440)
  const weekPrice = priceForPeriod(10080)
  const nauticalStats = product.nauticalDetails
    ? [
        { icon: Ruler, value: product.nauticalDetails.lengthMeters ? `${product.nauticalDetails.lengthMeters} m` : null, label: 'Eslora' },
        { icon: Users, value: product.nauticalDetails.capacity, label: 'Personas' },
        { icon: BedDouble, value: product.nauticalDetails.cabins, label: 'Camarotes' },
        { icon: Bath, value: product.nauticalDetails.bathrooms, label: 'Baños' },
      ].filter((item) => item.value !== null && item.value !== undefined)
    : []

  const changeImage = (event: React.SyntheticEvent, direction: -1 | 1) => {
    event.preventDefault()
    event.stopPropagation()
    setImageIndex((current) => (current + direction + images.length) % images.length)
  }

  // The card opens the preview drawer, but it stays a real link: crawlers can
  // reach the product page, and cmd/middle-click opens it in a new tab.
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const opensInNewTab =
      event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
    if (opensInNewTab || event.button !== 0 || !isAvailable) return

    event.preventDefault()
    onClick()
  }

  return (
    <Link href={href} onClick={handleClick} className="group block text-left w-full">
      <Card className="relative overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border border-border/50 hover:border-primary/20 bg-card p-0 gap-0">
        {/* Image container */}
        <div className="relative aspect-[16/10] overflow-hidden bg-muted">
          <ProductImage
            src={mainImage}
            alt={product.name}
            sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, 25vw"
            inset={false}
            className="transition-transform duration-500 group-hover:scale-105"
            containerClassName="absolute inset-0 rounded-none"
          />

          {images.length > 1 && (
            <>
              <span role="button" tabIndex={0} aria-label="Foto anterior" onClick={(event) => changeImage(event, -1)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') changeImage(event, -1) }} className="absolute left-2 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-background/90 text-foreground opacity-0 shadow-md transition-opacity group-hover:opacity-100 focus:opacity-100">
                <ChevronLeft className="size-4" />
              </span>
              <span role="button" tabIndex={0} aria-label="Foto siguiente" onClick={(event) => changeImage(event, 1)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') changeImage(event, 1) }} className="absolute right-2 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-background/90 text-foreground opacity-0 shadow-md transition-opacity group-hover:opacity-100 focus:opacity-100">
                <ChevronRight className="size-4" />
              </span>
              <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5 rounded-full bg-black/35 px-2 py-1">
                {images.map((_, index) => <span key={index} className={`size-1.5 rounded-full ${index === imageIndex ? 'bg-white' : 'bg-white/50'}`} />)}
              </div>
              <Badge className="absolute right-3 top-3 bg-background/90 text-foreground">{imageIndex + 1}/{images.length}</Badge>
            </>
          )}

          {/* Unavailable overlay */}
          {!isAvailable && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/90 backdrop-blur-sm">
              <Badge variant="failed" className="text-sm px-4 py-1.5">
                {tCatalog('unavailable')}
              </Badge>
            </div>
          )}

          {/* Quick action button */}
          {isAvailable && !product.nauticalDetails && (
            <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
              <Button className="w-full shadow-lg" render={<span />}>
                  <Calendar className="mr-2 h-4 w-4" />
                  {t('selectDates')}
              </Button>
            </div>
          )}

          {/* Discount badge */}
          {isAvailable && cardDiscount > 0 && product.quantity > 2 && (
            <Badge variant="progress" className="absolute top-3 left-3 text-xs font-medium">
              <TrendingDownSolidIcon className="h-3 w-3 mr-1" />-{Math.floor(cardDiscount)}%
            </Badge>
          )}
        </div>

        {/* Content */}
        <CardContent className="p-3">
          <h3 className="line-clamp-1 text-sm font-semibold transition-colors group-hover:text-primary md:text-base">
            {product.name}
          </h3>

          {nauticalStats.length > 0 && (
            <div className="mt-2 flex divide-x rounded-lg bg-muted/45 px-1 py-2 text-xs">
              {nauticalStats.map(({ icon: Icon, value, label }) => (
                <div key={label} className="flex min-w-0 flex-1 flex-col items-center gap-0.5 px-1" title={label} aria-label={`${value} ${label.toLowerCase()}`}>
                  <Icon className="text-primary size-3.5 shrink-0" />
                  <strong className="max-w-full truncate text-[11px] leading-tight">{value}</strong>
                </div>
              ))}
            </div>
          )}

          {product.nauticalDetails && (dayPrice !== null || weekPrice !== null) ? (
            <div className="mt-2 flex items-baseline gap-1 border-t pt-2">
              {dayPrice !== null && <><p className="text-primary text-base font-bold">{formatCurrency(dayPrice, currency)}</p><span className="text-muted-foreground text-[11px]">/ día</span></>}
              {weekPrice !== null && <p className="text-muted-foreground ml-auto text-xs"><strong className="text-foreground">{formatCurrency(weekPrice, currency)}</strong> / sem.</p>}
            </div>
          ) : <div className="mt-2 flex items-baseline gap-1">
            {pricingSummary.showStartingFrom && (
              <span className="text-xs md:text-sm text-muted-foreground">
                {t('startingFrom')}
              </span>
            )}
            <span className="text-lg md:text-xl font-bold text-primary">
              {formatCurrency(pricingSummary.displayPrice, currency)}
            </span>
            <span className="text-xs md:text-sm text-muted-foreground">
              / {periodLabel}
            </span>
          </div>
          }
        </CardContent>
      </Card>
    </Link>
  )
}

export function ProductGridWithPreview({
  products,
  storeSlug,
  basePath = '',
  businessHours,
  advanceNotice,
  minRentalMinutes = 0,
  timezone,
  initialProductId,
}: ProductGridWithPreviewProps) {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const initialOpenDone = useRef(false)

  // Auto-open modal when initialProductId is provided via query param
  useEffect(() => {
    if (initialProductId && !initialOpenDone.current) {
      const product = products.find((p) => p.id === initialProductId)
      if (product) {
        initialOpenDone.current = true
        setSelectedProduct(product)
        setIsModalOpen(true)
        // Clean up the query param from the URL without navigation
        const url = new URL(window.location.href)
        url.searchParams.delete('product')
        window.history.replaceState({}, '', url.toString())
      }
    }
  }, [initialProductId, products])

  const handleProductClick = (product: Product) => {
    setSelectedProduct(product)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedProduct(null)
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
        {products.map((product) => (
          <ProductCardInteractive
            key={product.id}
            product={product}
            href={`${basePath}/product/${product.id}`}
            onClick={() => handleProductClick(product)}
          />
        ))}
      </div>

      {selectedProduct && (
        <ProductPreviewModal
          product={selectedProduct}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          storeSlug={storeSlug}
          businessHours={businessHours}
          advanceNotice={advanceNotice}
          minRentalMinutes={minRentalMinutes}
          timezone={timezone}
        />
      )}
    </>
  )
}
